import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Item, ItemActions, ItemContent } from "@volca/ui/components/ui/item";
import { LogoIcon } from "@volca/ui/components/ui/logo";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { formatMoneyCents } from "@/core/utils/money";
import { InitiateVerificationResponse } from "@/payouts/payouts";

interface SelectPayoutTypeProps {
  verificationData: InitiateVerificationResponse;
  onSelectPaymentType: (type: "credit" | "cash") => void;
}

export const SelectPayoutType = ({
  verificationData,
  onSelectPaymentType,
}: SelectPayoutTypeProps) => {
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    "credit" | "cash" | null
  >(null);

  const handleSelect = () => {
    if (!selectedPaymentType) return;
    onSelectPaymentType(selectedPaymentType);
  };

  return (
    <Stack
      align="center"
      className="mx-auto min-h-screen w-full max-w-md bg-surface-raised p-8"
      gap={6}
    >
      <LogoIcon />
      <Text
        variant="title"
        render={<h1 />}
        className="mx-auto max-w-xs text-center"
      >
        Choose how to receive your{" "}
        {formatMoneyCents(verificationData?.payout_amount ?? 0)} referral
        payment
      </Text>
      <Stack className="w-full" align="start" gap={0} justify="center">
        <PayoutSelectionListItem
          title="Cash payment"
          onClick={() => setSelectedPaymentType("cash")}
          selected={selectedPaymentType === "cash"}
        />
        <Separator />
        <PayoutSelectionListItem
          title={`Credit with ${verificationData?.business_name}`}
          onClick={() => setSelectedPaymentType("credit")}
          selected={selectedPaymentType === "credit"}
        />
      </Stack>
      <Button
        onClick={handleSelect}
        disabled={!selectedPaymentType}
        className="w-full"
      >
        Next
      </Button>
      <Text variant="caption" className="text-center text-subtle">
        For cash payments you will be redirected to a third party payment
        provider.
      </Text>
    </Stack>
  );
};

interface PayoutSelectionListItemProps {
  title: string;
  onClick: () => void;
  selected?: boolean;
}

const PayoutSelectionListItem = ({
  title,
  onClick,
  selected = false,
}: PayoutSelectionListItemProps) => (
  <Item
    render={<button type="button" />}
    aria-pressed={selected}
    onClick={onClick}
    className={cn("rounded-none p-4 text-left", {
      "bg-selected": selected,
    })}
  >
    <ItemContent>
      <Text>{title}</Text>
    </ItemContent>
    {selected && (
      <ItemActions>
        <CheckIcon className="h-4 w-4" />
      </ItemActions>
    )}
  </Item>
);
